/**
 * VennDiagram — renders 2 or 3 overlapping circles with labels.
 *
 * data: {
 *   sets: [
 *     { label: "Sport", color: "#6366f1" },
 *     { label: "Musique", color: "#f59e0b" },
 *   ],
 *   zones: {
 *     "A": "10",        // only A
 *     "B": "7",         // only B
 *     "AB": "8",        // intersection A∩B
 *     "none": "5",      // outside all
 *     // For 3 sets:
 *     "C": "...", "AC": "...", "BC": "...", "ABC": "..."
 *   },
 *   total: 30,           // optional total shown
 *   showSolution: false,  // if true, show all zone values
 * }
 *
 * Zone keys: A, B, C (only), AB, AC, BC (pairs), ABC (triple), none (outside)
 * Values can be numbers or "?" for mystery
 */

const DEFAULT_COLORS = ['#6366f1', '#f59e0b', '#10b981'];

export default function VennDiagram({ data }) {
    const sets = data?.sets;
    const zones = data?.zones || {};
    if (!Array.isArray(sets) || sets.length < 2) return null;

    const is3 = sets.length >= 3;
    const showSolution = data.showSolution === true;
    const W = is3 ? 360 : 340;
    const H = is3 ? 280 : 220;
    const cx = W / 2;
    const cy = is3 ? H / 2 + 10 : H / 2 + 8;
    const r = is3 ? 70 : 75;

    // Circle positions — spread apart more to avoid label overlap
    const circles = is3 ? [
        { x: cx - 40, y: cy - 22, label: sets[0]?.label, color: sets[0]?.color || DEFAULT_COLORS[0] },
        { x: cx + 40, y: cy - 22, label: sets[1]?.label, color: sets[1]?.color || DEFAULT_COLORS[1] },
        { x: cx, y: cy + 30, label: sets[2]?.label, color: sets[2]?.color || DEFAULT_COLORS[2] },
    ] : [
        { x: cx - 48, y: cy, label: sets[0]?.label, color: sets[0]?.color || DEFAULT_COLORS[0] },
        { x: cx + 48, y: cy, label: sets[1]?.label, color: sets[1]?.color || DEFAULT_COLORS[1] },
    ];

    // Zone label positions (approximate)
    const zonePositions = is3 ? {
        'A': { x: cx - 72, y: cy - 38 },
        'B': { x: cx + 72, y: cy - 38 },
        'C': { x: cx, y: cy + 65 },
        'AB': { x: cx, y: cy - 38 },
        'AC': { x: cx - 34, y: cy + 20 },
        'BC': { x: cx + 34, y: cy + 20 },
        'ABC': { x: cx, y: cy },
        'none': { x: W - 25, y: H - 15 },
    } : {
        'A': { x: cx - 72, y: cy },
        'B': { x: cx + 72, y: cy },
        'AB': { x: cx, y: cy },
        'none': { x: W - 30, y: H - 15 },
    };

    const renderZoneValue = (key) => {
        const val = zones[key];
        if (val === undefined || val === null) return null;
        const isMystery = val === '?';
        const pos = zonePositions[key];
        if (!pos) return null;
        return (
            <text key={key} x={pos.x} y={pos.y}
                textAnchor="middle" dominantBaseline="central"
                className={`qv-venn__value ${isMystery ? 'qv-venn__value--mystery' : ''}`}>
                {val}
            </text>
        );
    };

    return (
        <div className="qv-venn">
            <svg viewBox={`0 0 ${W} ${H}`} className="qv-venn__svg" style={{ maxWidth: W }}>
                {/* Circles */}
                {circles.map((c, i) => {
                    let labelX = c.x;
                    let labelY = c.y - r - 10;
                    let anchor = 'middle';
                    if (!is3) {
                        // 2-set: left label goes left, right label goes right
                        labelX = i === 0 ? c.x - r * 0.3 : c.x + r * 0.3;
                        anchor = i === 0 ? 'end' : 'start';
                    } else if (i === 2) {
                        labelY = c.y + r + 14;
                    }
                    return (
                        <g key={i}>
                            <circle cx={c.x} cy={c.y} r={r}
                                className="qv-venn__circle"
                                style={{ stroke: c.color, fill: c.color }} />
                            <text x={labelX} y={labelY}
                                textAnchor={anchor} className="qv-venn__set-label">
                                {c.label}
                            </text>
                        </g>
                    );
                })}

                {/* Zone values */}
                {Object.keys(zones).map(renderZoneValue)}

                {/* Total */}
                {!!data.total && (
                    <text x={12} y={H - 8} className="qv-venn__total">
                        Total : {data.total}
                    </text>
                )}
            </svg>
        </div>
    );
}
